import React, {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useTelLinkClickedListener from 'flow/hooks/useTelLinkClickedListener';
import isElectron from 'is-electron';

import MainRoutes from 'flow/routes/MainRoutes';
import { NAV_TAB_ATTRIBUTE } from 'flow/components/ui-components/AppNavigation';

import { i18next } from 'flow/configure-locale';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import {
    clearState,
    setVisibility,
    setFocus,
    networkConnectionChange,
    checkTopConnection,
    setAudioInitiated,
    setPreventUnload,
    updateLayoutColumns,
} from 'flow/actions/app-actions';
import { fetchConnectedMediaDevices } from 'flow/actions/headset-actions';
import {
    answerOrHangupCall,
    setSoftphoneShouldBeRegistered,
    unregisterUserAgentBeforeQuit,
} from 'flow/actions/phone-actions';

import { electronActions, electronListeners } from 'flow/utils/electron-utils';
import {
    getSubscribeToUserOwnedErrandsCountPayload,
    getCentionUrl,
} from 'flow/utils/omnichannel-utils';
import {
    addGeneralLogEntry,
    addDeviceLogEntry,
    addPhoneLogEntry,
    addHeadsetLogEntry,
    addTopLogEntry,
    addMainProcessLogEntry,
    addOmniChannelLogEntry,
    addKuandoLogEntry,
    REGISTER_SOFTPHONE,
} from 'flow/utils/browser-utils';
import { attachIntegrationHostSystemListener } from 'flow/actions/cti-actions';
import {
    isEmbeddedTypeCti,
    isTeamsAnyVersion,
    isTeamsFullClient,
    setTitleAndFavIcon,
} from 'flow/utils/app-utils';
import HomeClient from 'flow/api-clients/home-client';
import { MainAudioContext } from 'resources/audio/wrapped-audio-context';
import { getPreventAppUnload } from 'flow/utils/appunload-helper';
import {
    fetchDomainBranding,
    checkRoamingStatus,
    fetchDomainBrandingLogoUrls,
    setDefaultBrandingData,
} from 'flow/actions/brandingV2-actions';
import AppContext from 'flow/contexts/app-context';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectUserHasCentionFeature } from 'flow/selectors/CentionOmniSelectors';
import DocumentTitleUserStatusHandler from 'flow/components/main/DocumentTitleUserStatusHandler';
import CompanionTraySync from 'flow/components/main/CompanionTraySync';
import ServiceUnavailableComponent from './electron/ServiceUnavailableComponent';
import { AuthServiceProvider } from 'flow/authservice/AuthServiceProvider';
import { USER_APP_SETTINGS_QUERY_KEY } from 'flow/hooks/api/useSoftphoneCallSettings';
import { useSyncFeatureFlags } from 'flow/hooks/api/useWebClientFeatureFlags';
import { initFaro, getFaro } from 'flow/api-clients/faro-client';
import { selectFaroObservabilityEnabled } from 'flow/selectors/feature-flag-selectors';
import {
    selectAnalyticsConsent,
    selectConsentCategories,
    selectIsLoggingOut,
} from 'flow/selectors/app-selectors';
import { useQueryClient } from '@tanstack/react-query';
import Modals from 'flow/components/modals/Modals';
import { useUserRegionData } from 'flow/hooks/region-lookup/useUserRegionData';
import Loading from 'flow/components/ui-components/Loading';
import LoadingOverlay from 'flow/components/ui-components/LoadingOverlay';

/**
 * Hiding the window drops focus, so on reveal Chromium assigns it to the first
 * focusable element — the `tabIndex={0}` wrapper Tooltip puts around the
 * leftmost nav tab, which then draws a focus ring and reads as a selected tab.
 *
 * Keyed off the focus rather than the reveal, so it needs no assumption about
 * when the window manager hands focus over. A pointer or key press marks the
 * focus that follows as the user's; focus arriving on a tab with nothing behind
 * it is the browser choosing, and is dropped. Clicks and keyboard navigation
 * keep their ring.
 */
const USER_INTENT_WINDOW_MS = 500;

let lastUserInputAt = 0;
const markUserInput = () => {
    lastUserInputAt = Date.now();
};

const dropFocusNotAskedFor = (event: FocusEvent) => {
    if (Date.now() - lastUserInputAt < USER_INTENT_WINDOW_MS) return;

    const target = event.target as HTMLElement | null;
    // Both directions: the focus lands on Tooltip's wrapper, whose child
    // carries the marker, but a tab reached by keyboard is the marked link
    // itself.
    const tab =
        target?.closest(`[${NAV_TAB_ATTRIBUTE}]`) ??
        target?.querySelector(`[${NAV_TAB_ATTRIBUTE}]`);
    if (tab) {
        target?.blur();
    }
};

const ContextComponent = () => {
    const isLoggedIn = useAppSelector(
        (state) => state.app.get('state') === 'ready',
    );
    useSyncFeatureFlags(isLoggedIn);

    const faroObservabilityEnabled = useAppSelector(
        selectFaroObservabilityEnabled,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const analyticsConsent = useAppSelector(selectAnalyticsConsent);
    const consentCategories = useAppSelector(selectConsentCategories);
    const isLoggingOut = useAppSelector(selectIsLoggingOut);

    const isErrorMonitoringAllowed =
        analyticsConsent === 'accepted' ||
        (analyticsConsent === 'partial' &&
            consentCategories?.get('errorMonitoring') === true);

    useEffect(() => {
        if (faroObservabilityEnabled && isErrorMonitoringAllowed) {
            initFaro();
            if (ownExtensionKey) {
                getFaro()?.api.setUser({ id: ownExtensionKey });
            }
        }
    }, [faroObservabilityEnabled, isErrorMonitoringAllowed, ownExtensionKey]);

    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [fetchedBranding, setFetchedBranding] = useState(false);
    const [notification, setNotification] = useState<{
        type: string;
        edit: boolean;
    } | null>(null);
    const [showDialpad, setShowDialpad] = useState(false);
    const [sleeping, setSleeping] = useState(false);
    const wakeupDetectorRef = useRef<NodeJS.Timeout | number>(null);

    const embedded = useAppSelector((state) => state.app.get('embedded'));

    const { regionData, isRegionCheckEnabled, isRegionInitialized } =
        useUserRegionData();

    const hasBrandingRunRef = useRef(false);
    const isLoggedInRef = useRef(isLoggedIn);

    isLoggedInRef.current = isLoggedIn;

    const isRoaming = useAppSelector((state) => state.app.get('isRoaming'));

    const userHasCentionOmniFeature = useAppSelector(
        selectUserHasCentionFeature,
    );

    const phoneEnabled = useAppSelector((state) => state.phone.get('enabled'));

    const centionUrl = useAppSelector(getCentionUrl);
    const omnichannelIframeRef = useAppSelector((state) =>
        state.omnichannel.get('iframeRef'),
    );

    const [theme] = React.useState(localStorage.getItem('theme') || 'light');

    const handleWakeup = useCallback(
        (sleepMilliseconds: number) => {
            if (!isLoggedIn) return;

            const sleepSeconds = Math.floor(sleepMilliseconds / 1000);
            const seconds = sleepSeconds % 60;
            const minutes = Math.floor(sleepSeconds / 60);
            const hours = Math.floor(sleepSeconds / (60 * 60));
            const duration =
                (hours ? hours + 'h ' : '') +
                (minutes ? minutes + 'm ' : '') +
                seconds +
                's';
            const unresponsiveLog = `App was unresponsive for ${duration}.`;
            const causeLog = `Detecting most likely cause: ${
                sleeping ? 'Sleep' : 'App freeze'
            }`;

            addGeneralLogEntry(`${unresponsiveLog} ${causeLog}`);
            setSleeping(false);

            dispatch(fetchConnectedMediaDevices);
            // The only sleep signal the web app gets when the OS never reported going offline
            dispatch(checkTopConnection());
            queryClient.invalidateQueries({
                queryKey: USER_APP_SETTINGS_QUERY_KEY,
            });

            if (userHasCentionOmniFeature && omnichannelIframeRef) {
                const payload = getSubscribeToUserOwnedErrandsCountPayload();
                addOmniChannelLogEntry(
                    'Triggering postMessage to Cention with payload: ',
                    payload,
                );
                try {
                    omnichannelIframeRef.contentWindow?.postMessage(
                        payload,
                        centionUrl,
                    );
                } catch (error) {
                    addOmniChannelLogEntry(
                        `Failed sending postMessage with error: ${error}`,
                    );
                }
            }
        },
        [
            isLoggedIn,
            sleeping,
            dispatch,
            queryClient,
            userHasCentionOmniFeature,
            omnichannelIframeRef,
            centionUrl,
        ],
    );

    useEffect(() => {
        const sleepCallback = () => {
            setSleeping(true);
            addGeneralLogEntry('System going into sleep...');
        };
        electronListeners.registerSleepListener(sleepCallback);
        electronListeners.registerLogListener(logEventCallback);
        window.document.addEventListener('drop', preventEvent);
        window.document.addEventListener('dragover', preventEvent);

        return () => {
            electronListeners.removeSleepListener(sleepCallback);
            electronListeners.removeLogListener(logEventCallback);
            window.document.removeEventListener('drop', preventEvent);
            window.document.removeEventListener('dragover', preventEvent);
        };
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (event: MediaQueryListEvent) => {
            const prefersDark = event.matches;
            if (theme === 'auto') {
                document.documentElement.classList.toggle('dark', prefersDark);
            }
        };

        if (theme === 'auto') {
            document.documentElement.classList.toggle(
                'dark',
                mediaQuery.matches,
            );
        } else if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        }

        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, [theme]);

    useEffect(() => {
        const WAIT_TIMEOUT = 20 * 1000;
        let lastTime = new Date().getTime();

        wakeupDetectorRef.current = setInterval(() => {
            const currentTime = new Date().getTime();
            if (currentTime > lastTime + WAIT_TIMEOUT + 2000) {
                handleWakeup(currentTime - lastTime);
            }
            lastTime = currentTime;
        }, WAIT_TIMEOUT);

        return () => {
            if (wakeupDetectorRef.current) {
                clearInterval(wakeupDetectorRef.current as number);
                wakeupDetectorRef.current = null;
            }
        };
    }, [handleWakeup]);

    useTelLinkClickedListener();

    useEffect(() => {
        const clearStateListener = () => {
            const preventUnload = getPreventAppUnload();
            if (preventUnload) {
                setTimeout(() => {
                    setPreventUnload(false);
                }, 500);
                return;
            }
            console.warn('unloading  -> triggering clearState');
            addGeneralLogEntry('Application closing');
            dispatch(clearState());
        };
        const registerSoftphone = () => {
            if (!wrappedLocalStorage.getItem(REGISTER_SOFTPHONE)) {
                dispatch(setSoftphoneShouldBeRegistered(true));
            }
        };

        if (!isEmbeddedTypeCti(embedded)) {
            window.addEventListener('beforeunload', clearStateListener);
        }
        if (isEmbeddedTypeCti(embedded)) {
            registerSoftphone();
            window.addEventListener('pagehide', clearStateListener);
            dispatch(attachIntegrationHostSystemListener());
        } else if (isTeamsAnyVersion(embedded)) {
            HomeClient.installSameSiteHeader();
            HomeClient.installWebappTtlHeader();
            if (isTeamsFullClient(embedded)) {
                registerSoftphone();
            }
        }

        return () => {
            if (isEmbeddedTypeCti(embedded)) {
                window.removeEventListener('pagehide', clearStateListener);
            } else {
                window.removeEventListener('beforeunload', clearStateListener);
            }
        };
    }, [dispatch, embedded]);

    useEffect(() => {
        const handleFocus = (e: FocusEvent) =>
            dispatch(setFocus(e.type === 'focus'));

        const handleVisibilityChange = () =>
            dispatch(
                setVisibility(window.document.visibilityState === 'visible'),
            );

        const phoneEventCallback = () => {
            addPhoneLogEntry(
                'globalShortcut alt+space or shift+space pressed to answer or hangup call',
            );
            dispatch(answerOrHangupCall);
        };

        const handleConnectionLost = () => {
            dispatch(networkConnectionChange(false));
            addGeneralLogEntry('Network connection lost');
        };

        const handleConnectionEstablished = () => {
            dispatch(networkConnectionChange(true));
            addGeneralLogEntry('Network connection established');
            dispatch(checkTopConnection());
        };

        // A socket can outlive the connection under it across a sleep without noticing.
        const handleSystemResume = () => {
            addGeneralLogEntry('System resumed from sleep');
            dispatch(checkTopConnection());
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleFocus);
        window.addEventListener('pointerdown', markUserInput, true);
        window.addEventListener('keydown', markUserInput, true);
        window.document.addEventListener('focusin', dropFocusNotAskedFor);
        window.document.addEventListener(
            'visibilitychange',
            handleVisibilityChange,
        );
        electronListeners.registerLogoutListener(dispatch);
        electronListeners.registerResumeListener(handleSystemResume);
        electronListeners.registerPhoneEventListener(phoneEventCallback);
        electronListeners.registerMeetingLobbyListener(dispatch);
        electronListeners.registerDeepLinkListener(dispatch);

        // On app quit, main asks us to cleanly unregister the softphone, then
        // waits for this ack before tearing the renderer down.
        const unregisterSoftphoneHandler = () => {
            Promise.resolve(dispatch(unregisterUserAgentBeforeQuit())).finally(
                () => electronActions.notifySoftphoneUnregistered(),
            );
        };
        electronListeners.registerUnregisterSoftphoneListener(
            unregisterSoftphoneHandler,
        );

        if (isElectron()) {
            HomeClient.installWebappTtlHeader();
        }

        if ('Notification' in window) {
            Notification.requestPermission();
        }

        window.addEventListener('offline', handleConnectionLost);
        window.addEventListener('online', handleConnectionEstablished);

        dispatch(setAudioInitiated(false));

        if (navigator.mediaDevices) {
            navigator.mediaDevices.ondevicechange = () => {
                addDeviceLogEntry('Device change detected');
                dispatch(fetchConnectedMediaDevices);
            };
        }

        MainAudioContext.onInitialContextRunning(() => {
            dispatch(setAudioInitiated(true));
        });

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleFocus);
            window.removeEventListener('pointerdown', markUserInput, true);
            window.removeEventListener('keydown', markUserInput, true);
            window.document.removeEventListener(
                'focusin',
                dropFocusNotAskedFor,
            );
            window.document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
            window.removeEventListener('offline', handleConnectionLost);
            window.removeEventListener('online', handleConnectionEstablished);
            electronListeners.removePhoneEventListener(phoneEventCallback);
            electronListeners.removeResumeListener(handleSystemResume);
            electronListeners.removeUnregisterSoftphoneListener(
                unregisterSoftphoneHandler,
            );
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(updateLayoutColumns());
    }, [dispatch]);

    /**
     * Run once after region initialization is complete. When region lookup is
     * enabled, waits for region data before fetching branding so the correct
     * regional endpoint is used. When region lookup is disabled,
     * isRegionInitialized defaults to true so branding fetches immediately.
     */
    useEffect(() => {
        if (!isRegionInitialized) return;
        if (isRegionCheckEnabled && regionData && !regionData.url) return;
        if (hasBrandingRunRef.current) return;
        hasBrandingRunRef.current = true;

        setDefaultBrandingData();
        checkRoamingStatus(dispatch);
        const canUseRegionCheck = isRegionCheckEnabled
            ? !regionData?.url
            : !isLoggedIn;

        Promise.all([
            fetchDomainBranding(canUseRegionCheck),
            fetchDomainBrandingLogoUrls(canUseRegionCheck),
        ]).then(() => {
            setFetchedBranding(true);
            if (
                !embedded ||
                isEmbeddedTypeCti(embedded) ||
                isTeamsFullClient(embedded)
            ) {
                setTitleAndFavIcon();
            }
        });
    }, [
        dispatch,
        embedded,
        isRegionInitialized,
        regionData,
        /**isLoggedIn is listed here so React doesn't warn about a missing
         * dependency (it's read inside the effect via the non-region path).
         * In practice the hasBrandingRunRef guard ensures this effect only
         * executes once — subsequent changes to isLoggedIn are no-ops.
         */
        isLoggedIn,
        isRegionCheckEnabled,
    ]);

    const contextValue = useMemo(
        () => ({
            i18next,
            embedded,
            phoneEnabled,
            notification,
            sendNotification: setNotification,
            showDialpad,
            setShowDialpad,
        }),
        [embedded, phoneEnabled, notification, showDialpad],
    );

    return (
        <AppContext.Provider value={contextValue}>
            {fetchedBranding ? (
                <AuthServiceProvider>
                    <DocumentTitleUserStatusHandler />
                    {isElectron() && <CompanionTraySync />}
                    {isRoaming ? (
                        <ServiceUnavailableComponent />
                    ) : (
                        <MainRoutes />
                    )}
                    <Modals />
                    {isLoggingOut && <LoadingOverlay />}
                </AuthServiceProvider>
            ) : (
                <Loading size={64} />
            )}
        </AppContext.Provider>
    );
};

export default ContextComponent;

const logEventCallback = (logInput: string, logType: string) => {
    switch (logType) {
        case 'headset':
            addHeadsetLogEntry(logInput);
            break;
        case 'top':
            addTopLogEntry(logInput);
            break;
        case 'device':
            addDeviceLogEntry(logInput);
            break;
        case 'phone':
            addPhoneLogEntry(logInput);
            break;
        case 'main':
            addMainProcessLogEntry(logInput);
            break;
        case 'kuando':
            addKuandoLogEntry(logInput);
            break;
        default:
            addGeneralLogEntry(logInput);
    }
};

const preventEvent = (e: Event) => e.preventDefault();
